import { useState } from 'react'
import useAuthUser from '@forwardflow/hooks/customHooks/useAuthUser'
import { useLocation } from 'react-router-dom'
import { Button, PageLoader } from '@forwardflow/ui'
import OrganizationIcon from '@forwardflow/assets/svg/OrganizationIcon'
import { UserService } from '@forwardflow/services/UserService'
import { message } from 'antd'
import { useAppStore } from '@forwardflow/store'
import { useUserNavigation } from '@forwardflow/hooks/customHooks/useUserNavigation'

const SelectUserOrganization = () => {
   const location = useLocation()
   const {
      data: user,
      isLoading: isUserLoading,
      mutate,
   } = useAuthUser({ revalidateOnMount: true })
   const { setCurrentOrganization } = useAppStore()
   const { navigateUser } = useUserNavigation()

   // Track when invitation mutations are in progress to prevent race conditions
   const [isMutatingInvitation, setIsMutatingInvitation] = useState(false)

   const handleSelect = (selectedOrgId: string) => {
      setCurrentOrganization(selectedOrgId)
      navigateUser({
         currentOrgId: selectedOrgId,
         searchParams: location.search,
      })
   }

   const AcceptOrganizationInvite = async (id: string) => {
      setIsMutatingInvitation(true)
      try {
         await UserService.acceptOrganizationInvitation(id)
         message.success('Invitation accepted successfully')
         setCurrentOrganization(id)
         await mutate()
      } catch (error) {
         console.log(error)
         message.error('Error accepting invitation')
      } finally {
         setIsMutatingInvitation(false)
      }
   }

   const DeclineOrganizationInvite = async (id: string) => {
      setIsMutatingInvitation(true)
      try {
         await UserService.rejectOrganizationInvitation(id)
         message.success('Invitation declined successfully')
         await mutate()
      } catch (error) {
         console.log(error)
         message.error('Error declining invitation')
      } finally {
         setIsMutatingInvitation(false)
      }
   }

   if (isUserLoading || isMutatingInvitation)
      return <PageLoader />

   // Show invitations if user has any
   if (user?.invitations?.length !== 0) {
      return (
         <div className="flex items-center justify-center min-h-screen">
            <div className="flex min-h-screen px-4 lg:px-[30px] flex-col items-center text-center pt-14 md:pt-28">
               <a href="/">
                  <img
                     width={'165px'}
                     src="/images/fff-logo.png"
                     className="md:absolute left-10 top-10"
                     alt="ff icon"
                  />
               </a>
            </div>
            <div className="w-full max-w-[680px] p-8 rounded-xl shadow-lg bg-[#222327]/80">
               <h1 className="mb-4 text-lg text-gray-300">
                  Organization Invitations Pending
               </h1>
               <h2 className="mb-8 text-3xl font-semibold text-white">
                  Accept an Organization to Join
               </h2>

               <div className="space-y-4">
                  {user?.invitations?.map((invite: any) => (
                     <div
                        key={invite.organizationId}
                        className="p-6 transition-all border border-gray-700 rounded-xl bg-gray-800/50 hover:border-primary/50"
                     >
                        <div className="flex items-center justify-between gap-x-8">
                           <div className="flex items-center gap-4">
                              <div>
                                 <p className="text-lg font-medium text-white">
                                    {invite?.organization.name}
                                 </p>
                                 <p className="text-sm text-gray-400">
                                    Click accept to join this organization
                                 </p>
                              </div>
                           </div>
                           <div className="flex items-center gap-3">
                              <Button
                                 onClick={() =>
                                    AcceptOrganizationInvite(
                                       invite?.organizationId
                                    )
                                 }
                                 className="px-6 py-2 font-medium bg-primary hover:bg-primary/90 min-w-48"
                              >
                                 Accept Invitation
                              </Button>
                              <Button
                                 onClick={() =>
                                    DeclineOrganizationInvite(
                                       invite?.organizationId
                                    )
                                 }
                                 className="px-6 py-2 text-red-500 bg-transparent border border-red-500/50 hover:bg-red-500/10"
                              >
                                 Decline
                              </Button>
                           </div>
                        </div>
                     </div>
                  ))}
               </div>
            </div>
         </div>
      )
   }

   if (user?.userOrganizations?.length === 1) return null

   return (
      <div className="flex items-center justify-center min-h-screen ">
         <div
            className={`flex min-h-screen px-4 lg:px-[30px] flex-col  items-center text-center pt-14 md:pt-28 `}
         >
            <a href="/">
               <img
                  width={'165px'}
                  src="/images/fff-logo.png"
                  className="md:absolute left-10 top-10"
                  alt="ff icon"
               />
            </a>
         </div>
         <div className="w-full max-w-[560px] p-8 rounded-xl shadow-lg bg-[#222327]/80 flex flex-col items-center">
            <div className="scale-[2.2] my-4">
               <OrganizationIcon />
            </div>

            <h3 className="my-6 text-2xl font-bold text-center text-[#C3C3C3]">
               Choose your workspace
            </h3>
            <p className="mb-10 text-center">
               You have access to multiple organizations. <br /> Select one to
               continue.
            </p>
            <p className="w-full bg-white/20 h-[1px] mb-8"></p>
            <ul className="w-full space-y-4">
               {user?.userOrganizations?.map((org: any) => (
                  <li
                     onClick={() => handleSelect(org.organizationId)}
                     key={org.organizationId}
                     className="flex items-center  p-2  h-[70px] rounded-md cursor-pointer bg-white/10 hover:bg-white/30"
                  >
                     <span className="bg-[#27282C] w-14 h-14 rounded-lg mr-5 flexc">
                        <OrganizationIcon />
                     </span>
                     <span className="font-medium text-white">
                        {org.organization.name}
                     </span>
                  </li>
               ))}
            </ul>
         </div>
      </div>
   )
}

export default SelectUserOrganization
